import type { PaymentIntegrationMap } from './payment'
import type {
  CovaMetadata,
  Delivery,
  DeliveryV2,
  Payments,
  WeekDay,
} from './warehouse'

export type PreviewFulfillmentType =
  | 'delivery'
  | 'pickup'
  | 'curbside'
  | 'mail_order'

export type PreviewSlot = 'ASAP' | [string, string] | string

export interface PreviewTimeSlotResponse {
  time_slots: Record<string, PreviewSlot[]>
}

export interface PreviewTimeSlotRequest {
  id: string
  type: PreviewFulfillmentType
  date_from: string
  offset: number
  canfleet_enable_delivery_hours?: boolean
  enable_instore_hours?: boolean
  enable_curbside_hours?: boolean
  delivery_display_asap_option?: string
  pickup_display_asap_option?: string
  curbside_display_asap_option?: string
  delivery?: Record<string, unknown>
  instore_pickup?: Record<string, unknown>
  curbside_pickup_hours?: Record<string, unknown>
  [key: string]: unknown
}

/**
 * @deprecated Legacy UI-shaped delivery zone — kept only so the read-only
 * Delivery tab table continues to compile while the new `/warehouse_zones`
 * pipeline (see `DeliveryZone` below) is wired in. Remove once the new card
 * (DeliveryZonesCard) ships.
 */
export interface DeliveryZoneRaw {
  id: string
  name: string
  zone_type: 'radius' | 'postal' | string
  radius?: number
  radius_unit?: 'km' | 'mi'
  postal_codes?: string[]
  fee: number
  min_purchase: number
  free_delivery_at: number
}

/**
 * @deprecated Same as `DeliveryZoneRaw` — temporary shim kept for the inline
 * read-only `ZonesTable` in `DeliveryTab.tsx`. The real per-warehouse zone
 * shape now lives in this file as `WarehouseZone` (+ per-type sub-types).
 */
export interface DeliveryZoneView {
  id: string
  name: string
  zoneType: 'radius' | 'postal' | string
  radius?: number
  radiusUnit?: 'km' | 'mi'
  postalCodes?: string[]
  fee: number
  minPurchase: number
  freeDeliveryAt: number
}

// ----- Warehouse-zone model (mirrors /warehouse_zones API + legacy adapters) -----

// Server-side `area_type` literal values. Mirrors legacy
// `~/constants/settings.ts → DELIVERY_ZONES_SETS`.
export type ZoneAreaType = 'radius' | 'postal_code' | 'polygon'
export type ZoneFeeUnit = 'fixed_amount' | 'percentage'
export type ZoneDistanceUnit = 'kilometer' | 'mile'
export type ZoneShapeType = 'polygon' | 'circle'

export interface LatLng {
  lat: number
  lng: number
}

export interface CircleBounds {
  center_lat?: number
  center_lng?: number
  radius?: number
}

export interface DeliveryFee {
  amount: number
  unit: ZoneFeeUnit
}

export interface MaxDeliveryDistance {
  amount: number
  unit: ZoneDistanceUnit
}

// Raw payload as the /warehouse_zones API returns it. Server stores percentage
// as a ratio (0.05 = 5%) and flattens fee unit / distance unit into `_type`
// siblings — see `zoneAdapters.ts` for the round-trip conversion.
export interface WarehouseZoneApi {
  _id: string
  name?: string
  enable?: boolean
  warehouse_id?: string
  area_type: ZoneAreaType
  delivery_minimum?: number | null
  delivery_free?: number | null
  delivery_fee?: number | null
  delivery_fee_type?: ZoneFeeUnit
  // Distance Radius
  max_delivery_distance?: number
  max_delivery_distance_type?: ZoneDistanceUnit
  // Postal Code
  postal_code?: string
  // Drawn Zone
  long_lat?: Array<[number, number]>
  shape_type?: ZoneShapeType
  circle_bounds?: CircleBounds
}

interface BaseZone {
  _id: string
  name: string
  enable: boolean
  warehouse_id?: string
  delivery_minimum: number
  delivery_free?: number
  delivery_fee: DeliveryFee
}

export interface DistanceRadiusZone extends BaseZone {
  area_type: 'radius'
  max_delivery_distance: MaxDeliveryDistance
}

export interface PostalCodeZone extends BaseZone {
  area_type: 'postal_code'
  postal_code: string
}

export interface DrawnZone extends BaseZone {
  area_type: 'polygon'
  latLngs: LatLng[]
  shape_type: ZoneShapeType
  circle_bounds: CircleBounds
}

export type DeliveryZone = DistanceRadiusZone | PostalCodeZone | DrawnZone

// Grouped view returned by `useDeliveryZones`.
export interface DeliveryZonesGrouped {
  all: DeliveryZone[]
  radius: DistanceRadiusZone[]
  postal: PostalCodeZone[]
  drawn: DrawnZone[]
}

// ----- Mail-order shipping zones (per-location admin-defined regions) -----
// Payload mirrors the PATCH body backend accepts on
// /warehouses/location_v2/:id under `location_data.mail_order_zones`.
// Country uses ISO-3166 alpha-2 ("CA" / "US"); state uses the local 2-letter
// province/state code. Money fields are persisted as decimal strings.
// `excluded_categories` (numeric category ids) replaces the location-level
// `excluded_mail_order_categories`: products in these categories can't be
// mail-ordered into this zone. Optional on the wire — older responses omit it;
// `mapToLocationDetail` normalizes it to `[]`.
export interface MailOrderShippingZone {
  name: string
  state: string
  country: string
  flat_rate: string
  minimum_order: string
  free_shipping_over: string
  excluded_categories?: number[]
  // Per-zone shipping tax override (decimal string, e.g. "13.00"). `null` /
  // absent = inherit the location-level `shipping_tax_percentage` at checkout.
  shipping_tax_percentage?: string | null
}

// Paged list response from GET /warehouse_zones/list?…
export interface WhZoneListResponse {
  items: WarehouseZoneApi[]
  total_pages?: number
  total_items?: number
  page?: number
  limit?: number
}

// Geo-position lookup for an address (POST /warehouses/find-position)
export interface FindPositionRequest {
  address_1?: string
  address_2?: string
  city?: string
  state?: string
  country?: string
  postal_code?: string
}

export interface FindPositionResponse {
  lat?: number
  lng?: number
  latitude?: number
  longitude?: number
}

export interface LocationDetailRaw {
  _id: string
  name: string
  cova_name?: string
  photo_url?: string
  address_1: string
  address_2?: string
  city: string
  state: string
  country: string
  postal_code: string
  phone_number: string
  email?: string | null
  license?: string
  status: 'synced' | string
  store_type: string
  last_synced_date?: string
  total_products?: number
  location_hours: Partial<Record<WeekDay, Array<{ from: string; to: string }>>>
  local_pickup: boolean
  curbside_pickup: boolean
  enable_drive_through_pickup: boolean
  enable_delivery: boolean
  enable_mail_order: boolean
  payments: Payments
  // Hours / delivery schedule
  delivery?: Delivery
  delivery_v2?: DeliveryV2
  delivery_scheduling_mode?: 'asap' | 'slots' | 'both'
  canfleet_enable_delivery_hours?: boolean
  required_delivery_time?: boolean
  delivery_display_asap_option?: '' | 'asap' | 'first'
  // Delivery zones
  delivery_zones?: DeliveryZoneRaw[]
  // Location-level delivery-zone variant selection. Mirrors the existing
  // `area_type` field on the warehouse model (`WHLocation.area_type`). Drives
  // which zone variant the Delivery Zones panel surfaces + creates by default.
  area_type?: ZoneAreaType
  delivery_linked?: string
  delivery_api_key?: string
  mail_order_linked?: string
  // Warehouse-zone polling flags (set true while a paste/import job is running)
  warehouse_zones_pasting?: boolean
  warehouse_zones_importing?: boolean
  // Warehouse geo (needed for the Delivery Zones map preview)
  latitude?: number
  longitude?: number
  // Mail order
  mail_order_flat_rate?: string | number
  mail_order_minimum_order?: string | number
  mail_order_free_shipping_over?: string | number
  mail_order_fee_type?: string
  // Per-location admin-defined shipping zones (new 2026.06.10)
  mail_order_zones?: MailOrderShippingZone[]
  // POS reserve quantity — products with on-hand <= this are hidden online
  low_stock_threshold?: number
  // Tips
  tip_percent_options?: string[]
  tip_enabled_fulfillment_types?: string[]
  // POS
  pos_id?: string
  cova_metadata?: CovaMetadata
  greenline_metadata?: {
    company_id?: string | number
    location_id?: string | number
  }
  post_metadata?: {
    company_id?: string | number
    location_id?: string | number
  }
  categories_mapped?: number
  // Categories excluded per fulfillment method (numeric category ids)
  excluded_local_pickup_categories?: number[]
  excluded_curbside_pickup_categories?: number[]
  excluded_local_delivery_categories?: number[]
  /** @deprecated superseded by per-zone `mail_order_zones[].excluded_categories` */
  excluded_mail_order_categories?: number[]
  location_status?: 'live' | string
  timezone?: string
  // Pantry compliance / tax (regulatory minimum age + shipping tax %)
  minimum_legal_age?: number
  shipping_tax_percentage?: string | number
  // Payment integration (org-level providers connected to this location)
  payment_integration?: PaymentIntegrationMap
  warehouse_id?: string
  [key: string]: unknown
}

export interface LocationDetail {
  id: string
  name: string
  posName: string
  picture?: string
  address1: string
  address2?: string
  city: string
  state: string
  country: string
  postalCode: string
  phone: string
  email: string
  license: string
  posConnected: boolean
  storeType: string
  lastSyncedDate: string
  totalProducts: number
  locationHours: Partial<Record<WeekDay, Array<{ from: string; to: string }>>>
  pickupEnabled: boolean
  curbsideEnabled: boolean
  driveThroughEnabled: boolean
  deliveryEnabled: boolean
  mailOrderEnabled: boolean
  payments: Payments
  // Hours / delivery
  delivery?: Delivery
  deliveryV2?: DeliveryV2
  schedulingMode: 'asap' | 'slots' | 'both'
  // Delivery zones & provider
  // NOTE: `deliveryZones` is the legacy read-only view used by the inline
  // DeliveryTab table. The new pipeline pulls zones live via `useDeliveryZones`.
  deliveryZones: DeliveryZoneView[]
  // Location-level delivery-zone variant selection (camelCase of raw `area_type`).
  // `null` when the location has never persisted a default.
  areaType: ZoneAreaType | null
  deliveryLinked?: string
  deliveryApiKey?: string
  mailOrderLinked?: string
  // Warehouse-zone polling flags + geo (used by the Delivery Zones card)
  warehouseZonesPasting: boolean
  warehouseZonesImporting: boolean
  latitude?: number
  longitude?: number
  // Mail order
  shippingFee: number
  minPurchaseMailOrder: number
  freeShippingAt: number
  // Per-location shipping zones (kept as raw API shape — admin-edited)
  mailOrderZones: MailOrderShippingZone[]
  // POS reserve threshold (0 = no items reserved; shown verbatim)
  lowStockThreshold: number
  // Tips
  tipsEnabled: boolean
  tipPresets: string[]
  // POS extra
  posId?: string
  posLocationId: string | null
  categoriesMapped: number
  // Categories excluded per fulfillment method (numeric category ids)
  excludedLocalPickupCategories: number[]
  excludedCurbsidePickupCategories: number[]
  excludedLocalDeliveryCategories: number[]
  /** @deprecated superseded by per-zone `mailOrderZones[].excluded_categories` */
  excludedMailOrderCategories: number[]
  locationStatus: string
  timezone: string
  // Pantry compliance / tax
  minimumLegalAge: number
  shippingTaxPercentage: string
  // Payment integration (raw map passed through for the Payments tab)
  paymentIntegration: PaymentIntegrationMap
  warehouseId: string
}

const ZONE_AREA_TYPES: readonly ZoneAreaType[] = [
  'radius',
  'postal_code',
  'polygon',
]

function isValidAreaType(value: unknown): value is ZoneAreaType {
  return (
    typeof value === 'string' &&
    (ZONE_AREA_TYPES as readonly string[]).includes(value)
  )
}

function deriveSchedulingMode(
  raw: LocationDetailRaw,
): 'asap' | 'slots' | 'both' {
  // Prefer the three explicit backend fields (what we now write); fall back to
  // the legacy `delivery_scheduling_mode` echo, then 'asap'.
  if (typeof raw.canfleet_enable_delivery_hours === 'boolean') {
    if (!raw.canfleet_enable_delivery_hours) return 'asap'
    return raw.delivery_display_asap_option === 'asap' ? 'both' : 'slots'
  }
  return raw.delivery_scheduling_mode ?? 'asap'
}

export function mapToLocationDetail(raw: LocationDetailRaw): LocationDetail {
  return {
    id: raw._id,
    name: raw.name,
    posName: raw.cova_name || raw.store_type || 'POS',
    picture: raw.photo_url,
    address1: raw.address_1,
    address2: raw.address_2,
    city: raw.city,
    state: raw.state,
    country: raw.country,
    postalCode: raw.postal_code,
    phone: raw.phone_number,
    email: raw.email ?? '',
    license: raw.license ?? '',
    posConnected: raw.status === 'synced',
    storeType: raw.store_type,
    lastSyncedDate: raw.last_synced_date ?? '',
    totalProducts: raw.total_products ?? 0,
    locationHours: raw.location_hours,
    pickupEnabled: raw.local_pickup,
    curbsideEnabled: raw.curbside_pickup,
    driveThroughEnabled: raw.enable_drive_through_pickup,
    deliveryEnabled: raw.enable_delivery,
    mailOrderEnabled: raw.enable_mail_order,
    payments: raw.payments,
    delivery: raw.delivery,
    deliveryV2: raw.delivery_v2,
    schedulingMode: deriveSchedulingMode(raw),
    deliveryZones: (raw.delivery_zones ?? []).map((z) => ({
      id: z.id,
      name: z.name,
      zoneType: z.zone_type,
      radius: z.radius,
      radiusUnit: z.radius_unit,
      postalCodes: z.postal_codes,
      fee: z.fee,
      minPurchase: z.min_purchase,
      freeDeliveryAt: z.free_delivery_at,
    })),
    areaType: isValidAreaType(raw.area_type) ? raw.area_type : null,
    deliveryLinked: raw.delivery_linked,
    deliveryApiKey: raw.delivery_api_key,
    mailOrderLinked: raw.mail_order_linked,
    warehouseZonesPasting: raw.warehouse_zones_pasting ?? false,
    warehouseZonesImporting: raw.warehouse_zones_importing ?? false,
    latitude: raw.latitude,
    longitude: raw.longitude,
    shippingFee: parseFloat(String(raw.mail_order_flat_rate ?? 0)) || 0,
    minPurchaseMailOrder:
      parseFloat(String(raw.mail_order_minimum_order ?? 0)) || 0,
    freeShippingAt:
      parseFloat(String(raw.mail_order_free_shipping_over ?? 0)) || 0,
    mailOrderZones: (raw.mail_order_zones ?? []).map((z) => ({
      ...z,
      excluded_categories: (z.excluded_categories ?? []).map(Number),
      // Backend stores Decimal128 (3 dp) and may echo a number or "13.000";
      // normalize to a string so the zone editor can round-trip it.
      shipping_tax_percentage:
        z.shipping_tax_percentage == null
          ? null
          : String(z.shipping_tax_percentage),
    })),
    lowStockThreshold:
      typeof raw.low_stock_threshold === 'number' ? raw.low_stock_threshold : 0,
    tipsEnabled: (raw.tip_enabled_fulfillment_types?.length ?? 0) > 0,
    tipPresets: raw.tip_percent_options ?? [],
    posId: raw.pos_id,
    posLocationId:
      (raw.cova_metadata?.LocationId != null
        ? String(raw.cova_metadata.LocationId)
        : null) ??
      (raw.greenline_metadata?.location_id != null
        ? String(raw.greenline_metadata.location_id)
        : null) ??
      (raw.post_metadata?.location_id != null
        ? String(raw.post_metadata.location_id)
        : null) ??
      null,
    categoriesMapped: raw.categories_mapped ?? 0,
    excludedLocalPickupCategories: (
      raw.excluded_local_pickup_categories ?? []
    ).map(Number),
    excludedCurbsidePickupCategories: (
      raw.excluded_curbside_pickup_categories ?? []
    ).map(Number),
    excludedLocalDeliveryCategories: (
      raw.excluded_local_delivery_categories ?? []
    ).map(Number),
    excludedMailOrderCategories: (raw.excluded_mail_order_categories ?? []).map(
      Number,
    ),
    locationStatus: raw.location_status ?? '',
    timezone: raw.timezone ?? '',
    minimumLegalAge: raw.minimum_legal_age ?? 19,
    shippingTaxPercentage:
      raw.shipping_tax_percentage != null
        ? String(raw.shipping_tax_percentage)
        : '0',
    paymentIntegration: raw.payment_integration ?? {},
    warehouseId: raw.warehouse_id ?? raw._id,
  }
}
